import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/shared/DataTable';
import { useToast } from '../../components/shared/Toast';
import { MdAdd, MdSearch, MdClose, MdEdit } from 'react-icons/md';
import { authenticatedFetch } from '../../lib/api';
import {
  ClearFiltersButton,
  PageSkeleton,
  erpPageBreadcrumbClass,
  erpPageBreadcrumbCurrentClass,
  erpPageBreadcrumbItemClass,
  erpPageBreadcrumbSepClass,
} from '@euroasiann/shared/ui';

// Types
interface Vessel {
    _id: string;
    name: string;
}

interface Organization {
    _id: string;
    name: string;
}

interface Incident {
    _id: string;
    incidentId: string;
    vesselId?: Vessel;
    customerId?: Organization;
    incidentType: string;
    severity: 'Low' | 'Medium' | 'High' | 'Critical';
    dateOccurred: string;
    location: string;
    description: string;
    immediateAction: string;
    status: 'Open' | 'In Progress' | 'Resolved' | 'Closed';
    resolutionDetails?: string;
}

export function IncidentManagementPage() {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    const [activeFilter, setActiveFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingIncident, setEditingIncident] = useState<Incident | null>(null);

    const [formData, setFormData] = useState({
        vesselId: '',
        customerId: '',
        incidentType: 'Operational',
        severity: 'Low',
        dateOccurred: new Date().toISOString().split('T')[0],
        location: '',
        description: '',
        immediateAction: '',
        status: 'Open',
        resolutionDetails: '',
    });

    // Fetch Incidents
    const { data: incidentsData, isLoading } = useQuery<Incident[]>({
        queryKey: ['tech-incidents'],
        queryFn: async () => {
            const response = await authenticatedFetch(`/api/v1/tech/incidents`);
            if (!response.ok) throw new Error('Failed to fetch incidents');
            const data = await response.json();
            return data.data || [];
        },
    });

    // Fetch Vessels for Dropdown
    const { data: vesselsData } = useQuery<Vessel[]>({
        queryKey: ['tech-vessels-all'],
        queryFn: async () => {
            const response = await authenticatedFetch(`/api/v1/tech/vessels`);
            if (!response.ok) return [];
            const data = await response.json();
            return data.data || [];
        },
        retry: false
    });

    // Fetch Organizations (Customers) for Dropdown
    const { data: organizationsData } = useQuery<Organization[]>({
        queryKey: ['tech-organizations-all'],
        queryFn: async () => {
            const response = await authenticatedFetch(`/api/v1/tech/organizations`);
            if (!response.ok) return [];
            const data = await response.json();
            return data.data || [];
        },
        retry: false
    });

    // Mutations
    const createMutation = useMutation({
        mutationFn: async (data: typeof formData) => {
            const payload = {
                vessel_id: data.vesselId,
                customer_id: data.customerId || undefined,
                incident_type: data.incidentType,
                severity: data.severity,
                date_occurred: new Date(data.dateOccurred).toISOString(),
                location: data.location,
                description: data.description,
                immediate_action: data.immediateAction,
                status: data.status,
                resolution_details: data.resolutionDetails || undefined
            };

            console.log('--- TECH CREATE INCIDENT PAYLOAD ---', payload);

            const response = await authenticatedFetch('/api/v1/tech/incidents', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.message || 'Failed to create incident');
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tech-incidents'] });
            showToast('Incident created successfully', 'success');
            handleCloseModal();
        },
        onError: (error: any) => {
            showToast(error.message, 'error');
        },
    });

    const updateMutation = useMutation({
        mutationFn: async ({ id, data }: { id: string; data: Partial<typeof formData> }) => {
            const payload = {
                ...(data.vesselId && { vessel_id: data.vesselId }),
                ...(data.customerId !== undefined && { customer_id: data.customerId || undefined }),
                ...(data.incidentType && { incident_type: data.incidentType }),
                ...(data.severity && { severity: data.severity }),
                ...(data.dateOccurred && { date_occurred: new Date(data.dateOccurred).toISOString() }),
                ...(data.location && { location: data.location }),
                ...(data.description !== undefined && { description: data.description }),
                ...(data.immediateAction !== undefined && { immediate_action: data.immediateAction }),
                ...(data.status && { status: data.status }),
                ...(data.resolutionDetails !== undefined && { resolution_details: data.resolutionDetails || undefined })
            };

            console.log('--- TECH UPDATE INCIDENT PAYLOAD ---', payload);

            const response = await authenticatedFetch(`/api/v1/tech/incidents/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.message || 'Failed to update incident');
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tech-incidents'] });
            showToast('Incident updated successfully', 'success');
            handleCloseModal();
        },
        onError: (error: any) => {
            showToast(error.message, 'error');
        },
    });

    // Derived state
    const filteredIncidents = useMemo(() => {
        let baseData = incidentsData || [];

        if (activeFilter !== 'all') {
            baseData = baseData.filter(i => i.status.toLowerCase() === activeFilter.toLowerCase() ||
                (activeFilter === 'in_progress' && i.status === 'In Progress'));
        }

        if (!searchQuery.trim()) return baseData;

        const query = searchQuery.toLowerCase();
        return baseData.filter(
            (incident) =>
                incident.incidentId?.toLowerCase().includes(query) ||
                incident.vesselId?.name?.toLowerCase().includes(query) ||
                incident.customerId?.name?.toLowerCase().includes(query) ||
                incident.description?.toLowerCase().includes(query) ||
                incident.incidentType?.toLowerCase().includes(query)
        );
    }, [incidentsData, searchQuery, activeFilter]);

    const filters = [
        { id: 'all', label: 'All Incidents' },
        { id: 'open', label: 'Open' },
        { id: 'in_progress', label: 'In Progress' },
        { id: 'resolved', label: 'Resolved' },
        { id: 'closed', label: 'Closed' },
    ];

    // Logic
    const handleOpenModal = (incident?: Incident) => {
        if (incident) {
            setEditingIncident(incident);
            setFormData({
                vesselId: incident.vesselId?._id || '',
                customerId: incident.customerId?._id || '',
                incidentType: incident.incidentType,
                severity: incident.severity,
                dateOccurred: incident.dateOccurred
                    ? new Date(incident.dateOccurred).toISOString().split('T')[0]
                    : new Date().toISOString().split('T')[0],
                location: incident.location || '',
                description: incident.description || '',
                immediateAction: incident.immediateAction || '',
                status: incident.status,
                resolutionDetails: incident.resolutionDetails || '',
            });
        } else {
            setEditingIncident(null);
            setFormData({
                vesselId: '',
                customerId: '',
                incidentType: 'Operational',
                severity: 'Low',
                dateOccurred: new Date().toISOString().split('T')[0],
                location: '',
                description: '',
                immediateAction: '',
                status: 'Open',
                resolutionDetails: '',
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingIncident(null);
    };

    const handleSubmit = () => {
        if (editingIncident) {
            updateMutation.mutate({ id: editingIncident._id, data: formData });
        } else {
            createMutation.mutate(formData);
        }
    };

    const columns = [
        {
            key: 'incidentId',
            header: 'Incident ID',
            render: (incident: Incident) => (
                <span className="font-semibold text-[hsl(var(--primary))]">{incident.incidentId}</span>
            ),
        },
        {
            key: 'vesselId',
            header: 'Vessel',
            render: (incident: Incident) => (
                <span className="text-[hsl(var(--foreground))]">{incident.vesselId?.name || 'N/A'}</span>
            ),
        },
        {
            key: 'type',
            header: 'Type',
            render: (incident: Incident) => (
                <span className="text-[hsl(var(--muted-foreground))]">{incident.incidentType}</span>
            ),
        },
        {
            key: 'severity',
            header: 'Severity',
            render: (incident: Incident) => (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${incident.severity === 'Critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400' :
                    incident.severity === 'High' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-400' :
                        incident.severity === 'Medium' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400' :
                            'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400'
                    }`}>
                    {incident.severity}
                </span>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            render: (incident: Incident) => (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ${incident.status === 'Closed' ? 'bg-[#0088cc]/10 text-[hsl(var(--foreground))] dark:bg-slate-800 dark:text-[#0088cc]/65' :
                    incident.status === 'Resolved' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400' :
                        incident.status === 'In Progress' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400' :
                            'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-400'
                    }`}>
                    {incident.status}
                </span>
            ),
        },
        {
            key: 'dateOccurred',
            header: 'Date',
            render: (incident: Incident) => (
                <span className="text-[hsl(var(--muted-foreground))]">
                    {incident.dateOccurred ? new Date(incident.dateOccurred).toLocaleDateString() : 'N/A'}
                </span>
            ),
        },
        {
            key: 'actions',
            header: 'Action',
            render: (incident: Incident) => (
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handleOpenModal(incident)}
                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Edit"
                    >
                        <MdEdit className="w-5 h-5" />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="w-full min-h-screen p-8 bg-[hsl(var(--background))]">
            <div className="mb-6 flex items-center justify-between">
                <div>
                    <nav className={erpPageBreadcrumbClass} aria-label="Breadcrumb">
                        <span className={erpPageBreadcrumbItemClass}>Tech</span>
                        <span className={erpPageBreadcrumbSepClass}>/</span>
                        <span className={erpPageBreadcrumbCurrentClass}>Incident Management</span>
                    </nav>
                    <h1 className="text-3xl font-bold text-[hsl(var(--foreground))]">Incidents</h1>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    className="flex items-center gap-2 px-4 py-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-lg transition-colors"
                >
                    <MdAdd className="w-5 h-5" />
                    Create Incident
                </button>
            </div>

            {/* Filter Tabs */}
            <div className="mb-6 flex flex-wrap gap-2 items-center overflow-x-auto">
                {filters.map((filter) => (
                    <button
                        key={filter.id}
                        onClick={() => setActiveFilter(filter.id)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeFilter === filter.id
                            ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
                            : 'bg-[hsl(var(--card))] text-[hsl(var(--foreground))] border border-[hsl(var(--border))] hover:bg-[hsl(var(--muted))]'
                            }`}
                    >
                        {filter.label}
                    </button>
                ))}
                <ClearFiltersButton
                    visible={searchQuery.trim() !== '' || activeFilter !== 'all'}
                    onClick={() => {
                        setSearchQuery('');
                        setActiveFilter('all');
                    }}
                    variant="toolbar"
                />
            </div>

            {/* Search Bar */}
            <div className="mb-6">
                <div className="relative">
                    <MdSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[hsl(var(--muted-foreground))] w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Search by ID, vessel, customer, type or description..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
                    />
                </div>
            </div>

            {/* Table */}
            {isLoading ? (
                <PageSkeleton variant="table" />
            ) : (
                <div className="bg-[hsl(var(--card))] rounded-lg border border-[hsl(var(--border))] overflow-hidden">
                    <DataTable
                        columns={columns}
                        data={filteredIncidents}
                        emptyMessage="No incidents found."
                    />
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md overflow-y-auto py-10">
                    <div className="bg-[hsl(var(--card))] rounded-lg shadow-xl w-full max-w-3xl mx-4 border border-[hsl(var(--border))] my-auto">
                        <div className="flex items-center justify-between p-6 border-b border-[hsl(var(--border))]">
                            <h2 className="text-xl font-bold text-[hsl(var(--foreground))]">
                                {editingIncident ? 'Edit Incident' : 'Create New Incident'}
                            </h2>
                            <button
                                onClick={handleCloseModal}
                                className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                            >
                                <MdClose className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                            {/* Vessel Input */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Vessel</label>
                                    <select
                                        value={formData.vesselId}
                                        onChange={(e) => setFormData({ ...formData, vesselId: e.target.value })}
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    >
                                        <option value="">Select Vessel</option>
                                        {vesselsData?.map((v) => (
                                            <option key={v._id} value={v._id}>{v.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Customer</label>
                                    <select
                                        value={formData.customerId}
                                        onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    >
                                        <option value="">Select Customer (Optional)</option>
                                        {organizationsData?.map((org) => (
                                            <option key={org._id} value={org._id}>{org.name}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* Levels & Type */}
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Incident Type</label>
                                    <select
                                        value={formData.incidentType}
                                        onChange={(e) => setFormData({ ...formData, incidentType: e.target.value })}
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    >
                                        <option value="Operational">Operational</option>
                                        <option value="Environmental">Environmental</option>
                                        <option value="Technical">Technical</option>
                                        <option value="Security">Security</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Severity</label>
                                    <select
                                        value={formData.severity}
                                        onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    >
                                        <option value="Low">Low</option>
                                        <option value="Medium">Medium</option>
                                        <option value="High">High</option>
                                        <option value="Critical">Critical</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Status</label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    >
                                        <option value="Open">Open</option>
                                        <option value="In Progress">In Progress</option>
                                        <option value="Resolved">Resolved</option>
                                        <option value="Closed">Closed</option>
                                    </select>
                                </div>
                            </div>

                            {/* Date & Location */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Date Occurred</label>
                                    <input
                                        type="date"
                                        value={formData.dateOccurred}
                                        onChange={(e) => setFormData({ ...formData, dateOccurred: e.target.value })}
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Location</label>
                                    <input
                                        type="text"
                                        value={formData.location}
                                        onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                        placeholder="E.g., Engine Room, Deck..."
                                    />
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Description</label>
                                <textarea
                                    className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    rows={3}
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Describe the incident..."
                                />
                            </div>

                            {/* Immediate Action */}
                            <div>
                                <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Immediate Action Taken</label>
                                <textarea
                                    className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                    rows={2}
                                    value={formData.immediateAction}
                                    onChange={(e) => setFormData({ ...formData, immediateAction: e.target.value })}
                                    placeholder="What was done immediately..."
                                />
                            </div>

                            {/* Resolution Details */}
                            {['Resolved', 'Closed'].includes(formData.status) && (
                                <div>
                                    <label className="block text-sm font-medium text-[hsl(var(--foreground))] mb-2">Resolution Details</label>
                                    <textarea
                                        className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg bg-[hsl(var(--background))] text-[hsl(var(--foreground))] focus:ring-2 focus:ring-[hsl(var(--primary))]"
                                        rows={2}
                                        value={formData.resolutionDetails}
                                        onChange={(e) => setFormData({ ...formData, resolutionDetails: e.target.value })}
                                        placeholder="How was it resolved..."
                                    />
                                </div>
                            )}

                        </div>

                        <div className="flex items-center justify-end gap-3 p-6 border-t border-[hsl(var(--border))]">
                            <button
                                onClick={handleCloseModal}
                                disabled={createMutation.isPending || updateMutation.isPending}
                                className="px-4 py-2 border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-lg hover:bg-[hsl(var(--muted))] transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={createMutation.isPending || updateMutation.isPending}
                                className="px-4 py-2 bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] rounded-lg transition-colors flex items-center gap-2"
                            >
                                {(createMutation.isPending || updateMutation.isPending) && (
                                    <div className="w-4 h-4 border-2 border-[hsl(var(--primary-foreground))] border-t-transparent rounded-full animate-spin" />
                                )}
                                {editingIncident ? 'Update Incident' : 'Create Incident'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
